import type SubstrateSearchRequest from '../../data/schema/SubstrateSearchRequest';
import type { SearchResultConversation } from '../../data/schema/SubstrateSearchResponse';
import type { ConversationType } from 'owa-graph-schema';
import {
    convertRecipientToSingleRecipientType,
    convertSearchSortOrder,
    convertToFolderId,
    convertToItemId,
    convertToItemIds,
    convertUtcDateTimeToRequestTimeZone,
} from './converterHelpers';
import type { Result } from '@1js/3s-schema';
import { logUsage } from 'owa-analytics';

export default function convertSearchResultConversationToConversationType(
    searchResult: Result,
    request: SubstrateSearchRequest
): ConversationType | undefined {
    const searchConversation = searchResult.Source as SearchResultConversation;
    // Ensure required properties are present
    if (
        !searchConversation.ConversationId ||
        searchConversation.UnreadCount === undefined ||
        searchConversation.UnreadCount === null
    ) {
        /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
         * BASELINE: DO NOT COPY/PASTE!
         *	> Argument at index 1 for logUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
        logUsage('SearchResultConversion_ConversationType_MissingRequiredProperties', undefined, {
            missingConversationId: !searchConversation.ConversationId,
            missingUnreadCount:
                searchConversation.UnreadCount === undefined ||
                searchConversation.UnreadCount === null,
        });
        return undefined;
    }

    const conversationId = convertToItemId(searchConversation.ConversationId);

    return {
        __typename: 'ConversationType',
        id: conversationId?.Id,
        Categories: searchConversation.Categories,
        ConversationId: conversationId,
        ConversationTopic: searchConversation.ConversationTopic ?? '',
        FlagStatus: searchConversation.FlagStatus,
        From: searchConversation?.From
            ? convertRecipientToSingleRecipientType(searchConversation.From)
            : undefined,
        GlobalItemIds: searchConversation?.GlobalItemIds
            ? convertToItemIds(searchConversation.GlobalItemIds)
            : undefined,
        GlobalMessageCount: 2, // The message list uses this field to determine if there should be a conversation expansion button.
        // We don't have this information from the search service, so we just set it to 2 to always show the button.
        GlobalUnreadCount: searchConversation.GlobalUnreadCount,
        HasAttachments: searchConversation.HasAttachments,
        HasIrm: searchConversation.HasIrm,
        HasProcessedSharepointLink: searchConversation.HasProcessedSharepointLink,
        Importance: searchConversation.Importance,
        ItemClasses: searchConversation.ItemClasses,
        ItemIds: searchConversation?.ItemIds
            ? convertToItemIds(searchConversation.ItemIds)
            : undefined,
        LastDeliveryOrRenewTime: convertUtcDateTimeToRequestTimeZone(
            searchConversation.LastDeliveryOrRenewTime ?? searchConversation.LastDeliveryTime ?? '',
            request
        ),
        LastDeliveryTime: convertUtcDateTimeToRequestTimeZone(
            searchConversation.LastDeliveryTime ?? '',
            request
        ),
        LastModifiedTime: searchConversation?.LastModifiedTime
            ? convertUtcDateTimeToRequestTimeZone(searchConversation.LastModifiedTime, request)
            : undefined,
        MailboxGuids: searchConversation.MailboxGuids,
        MessageCount: searchConversation.MessageCount,
        ParentFolderId: searchConversation?.ParentFolderId
            ? convertToFolderId(searchConversation.ParentFolderId)
            : undefined,
        Preview: searchConversation.Preview,
        SortOrderSource: searchConversation?.SortOrderSource
            ? convertSearchSortOrder(searchConversation.SortOrderSource)
            : undefined,
        UniqueRecipients: searchConversation.UniqueRecipients,
        UniqueSenders: searchConversation.UniqueSenders,
        UnreadCount: searchConversation.UnreadCount,
    };
}
